import { useCallback, useState } from 'react';
import { Button, Group, Modal, Radio, Stack, Text } from '@mantine/core';
import type { IResultadoCargaMasiva, MatrizCargaMasiva } from '../interfaces';
import { ModalResultadoCargaMasivaVista } from '../vista/modalResultadoCargaMasivaVista';
import { CotizadorModelo } from '../../../modelos/CotizadorNBS';
import { useNotificaciones } from '../../../globales/contextos/notificationContext';
import { useCargando } from '../../../globales/contextos/cargandoContext';
import { usePaisConfiguracion } from './paisConfiguracion';
import { EnumTipoNotificacion } from '../../../globales/interfaces';

interface OpcionesCargaMasiva {
  matriz: MatrizCargaMasiva;
  /** Título de la columna que identifica la fila en el resumen de rechazadas (ej. "Combinación", "Mes"). */
  etiquetaReferencia: string;
  /** Se llama antes de cargar (ej. cancelar una edición en curso). */
  antesDeCargar?: () => void;
  /** Se llama cuando la carga termina y hay filas guardadas (ej. recargar la tabla). */
  alTerminar: () => void | Promise<void>;
}

type ModoCarga = 'agregar' | 'reemplazar';

/** Cuándo una fila del archivo "ya existe" en cada matriz (la clave con la que el back la busca). */
const CLAVE_FILA: Record<MatrizCargaMasiva, string> = {
  'logistica': 'misma combinación de producto, máquina, operador y paradas',
  'moi-ascensor': 'mismo número de paradas',
  'moi-escalera': 'misma altura',
  'variacion-mensual': 'mismo mes',
  'comision': 'mismo margen',
};

const filas = (n: number) => (n === 1 ? '1 fila' : `${n} filas`);

/**
 * Descarga de plantilla y carga masiva de una matriz de configuración (mismo flujo que la carga de Tareas).
 * Al elegir el archivo se simula la carga (no guarda nada):
 * - Si no toca datos existentes (matriz vacía o solo filas nuevas sin sobrantes), se carga directo.
 * - Si sobrescribe filas o hay filas que no vienen en el archivo, se muestra qué pasará y el usuario decide.
 */
export const useCargaMasivaMatriz = ({ matriz, etiquetaReferencia, antesDeCargar, alTerminar }: OpcionesCargaMasiva) => {
  const { mostrarNotificacion } = useNotificaciones();
  const { manejarCargando } = useCargando();
  const paisId = usePaisConfiguracion();
  const [resultado, setResultado] = useState<IResultadoCargaMasiva | null>(null);
  const [pendiente, setPendiente] = useState<{ archivo: File; simulacion: IResultadoCargaMasiva } | null>(null);
  const [modo, setModo] = useState<ModoCarga>('agregar');
  // Aviso para el resumen cuando no se aplicó nada (p. ej. reemplazar con filas con errores).
  const [avisoResultado, setAvisoResultado] = useState<string | undefined>();

  const descargarPlantilla = useCallback(async () => {
    try {
      manejarCargando(true, undefined, true);
      await new CotizadorModelo().descargarPlantillaMatriz(matriz, paisId);
    } catch (error) {
      console.error(`Error al descargar la plantilla de ${matriz}:`, error);
      mostrarNotificacion(EnumTipoNotificacion.Error, 'Error al descargar', 'No fue posible descargar la plantilla. Intenta nuevamente.');
    } finally {
      manejarCargando(false);
    }
  }, [matriz, paisId, manejarCargando, mostrarNotificacion]);

  /** Carga real. reemplazar: además borra las filas que no vienen en el archivo. */
  const aplicar = useCallback(async (archivo: File, reemplazar: boolean) => {
    try {
      manejarCargando(true, 'Procesando carga masiva...', true);
      const respuesta = await new CotizadorModelo().cargaMasivaMatriz(matriz, paisId as number, archivo, reemplazar, false);
      const datos = respuesta.Data;
      mostrarNotificacion(
        datos?.FilasRechazadas ? EnumTipoNotificacion.Warning : EnumTipoNotificacion.Success,
        'Carga masiva',
        respuesta?.Messages?.[0] ?? 'Carga masiva finalizada.'
      );
      if (datos) {
        // Reemplazar solo se aplica con el archivo sin errores: se explica junto al detalle de los errores.
        setAvisoResultado(!datos.Aplicada && datos.FilasRechazadas > 0
          ? (reemplazar
            ? `No se reemplazó la matriz: para reemplazarla, el archivo no puede tener filas con errores (tiene ${filas(datos.FilasRechazadas)}). Corrígelas y vuelve a cargarlo; la matriz no tuvo cambios.`
            : 'No se guardó ninguna fila: todas tienen errores. Corrígelas y vuelve a cargar el archivo.')
          : undefined);
        setResultado(datos);
        if (datos.Aplicada) await alTerminar();
      }
    } catch (error: any) {
      console.error(`Error en la carga masiva de ${matriz}:`, error);
      mostrarNotificacion(EnumTipoNotificacion.Error, 'Error en la carga', error?.mensajeBackend || 'No fue posible procesar el archivo.');
    } finally {
      manejarCargando(false);
    }
  }, [matriz, paisId, alTerminar, manejarCargando, mostrarNotificacion]);

  /** Simula la carga para saber si toca datos existentes antes de preguntar. */
  const analizar = useCallback(async (archivo: File) => {
    antesDeCargar?.();
    let simulacion: IResultadoCargaMasiva | undefined;
    try {
      manejarCargando(true, 'Revisando el archivo...', true);
      simulacion = (await new CotizadorModelo().cargaMasivaMatriz(matriz, paisId as number, archivo, true, true)).Data;
    } catch (error: any) {
      console.error(`Error al revisar el archivo de ${matriz}:`, error);
      mostrarNotificacion(EnumTipoNotificacion.Error, 'Error en la carga', error?.mensajeBackend || 'No fue posible procesar el archivo.');
      return;
    } finally {
      manejarCargando(false);
    }
    if (!simulacion) return;

    // No sobrescribe nada ni sobra nada: no hay nada que decidir.
    if (simulacion.FilasActualizadas === 0 && simulacion.FilasEliminadas === 0) {
      await aplicar(archivo, false);
      return;
    }
    setModo('agregar');
    setPendiente({ archivo, simulacion });
  }, [matriz, paisId, antesDeCargar, aplicar, manejarCargando, mostrarNotificacion]);

  const cargaMasiva = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx';
    input.onchange = (e: Event) => {
      const archivo = (e.target as HTMLInputElement).files?.[0];
      if (archivo) analizar(archivo);
    };
    input.click();
  }, [analizar]);

  const continuar = () => {
    if (!pendiente) return;
    const { archivo } = pendiente;
    setPendiente(null);
    aplicar(archivo, modo === 'reemplazar');
  };

  const sim = pendiente?.simulacion;
  const sobran = sim?.FilasEliminadas ?? 0;

  const modalResultado = (
    <>
      <Modal opened={pendiente !== null} onClose={() => setPendiente(null)} title={<Text fw={600}>Carga masiva</Text>} centered>
        {sim && (
          <Stack gap="md">
            <Text size="sm">
              El archivo trae {filas(sim.FilasCreadas)} {sim.FilasCreadas === 1 ? 'nueva' : 'nuevas'} y{' '}
              {filas(sim.FilasActualizadas)} que ya {sim.FilasActualizadas === 1 ? 'existe' : 'existen'} en la matriz ({CLAVE_FILA[matriz]}).
              {sobran > 0 && <> En la matriz hay {filas(sobran)} que no {sobran === 1 ? 'viene' : 'vienen'} en el archivo.</>}
            </Text>

            {sobran > 0 ? (
              <Radio.Group value={modo} onChange={(valor) => setModo(valor as ModoCarga)} label="¿Cómo quieres aplicar el archivo?">
                <Stack gap="sm" mt="xs">
                  <Radio
                    value="agregar"
                    label="Agregar y actualizar"
                    description={`Se agregan las filas nuevas y las que ya existen se sobrescriben con los valores del archivo. Las ${filas(sobran)} que no vienen en el archivo se conservan sin cambios.`}
                  />
                  <Radio
                    value="reemplazar"
                    label="Reemplazar toda la matriz"
                    description={`La matriz queda igual al archivo: se agregan las filas nuevas, se sobrescriben las existentes y se eliminan las ${filas(sobran)} que no vienen en el archivo. No se puede deshacer.`}
                  />
                </Stack>
              </Radio.Group>
            ) : (
              <Text size="sm">Se agregarán las filas nuevas y las que ya existen se sobrescribirán con los valores del archivo.</Text>
            )}

            <Group justify="flex-end" gap="xs">
              <Button variant="light" color="primary" size="xs" radius="xl" onClick={() => setPendiente(null)}>Cancelar</Button>
              <Button color="primary" size="xs" radius="xl" onClick={continuar}>
                {modo === 'reemplazar' ? 'Reemplazar matriz' : 'Cargar archivo'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      <ModalResultadoCargaMasivaVista
        abrir={resultado !== null && resultado.FilasRechazadas > 0}
        onClose={() => setResultado(null)}
        resultado={resultado}
        etiquetaReferencia={etiquetaReferencia}
        aviso={avisoResultado}
      />
    </>
  );

  return { descargarPlantilla, cargaMasiva, modalResultado };
};
